import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useDatabase } from '../../context/DatabaseContext';

const { FiGrid, FiInfo, FiFilter } = FiIcons;

const ETIMClassification = ({ onCategorySelect }) => {
  const { getCategories } = useDatabase();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const categories = getCategories();

  const etimHierarchy = {
    'EC0000': {
      name: 'Wälzlagertechnik',
      description: 'Rotationslager für radiale und axiale Belastungen',
      subcategories: {
        'EC000030': {
          name: 'Rillenkugellager',
          technical: 'Punktkontakt, hohe Drehzahlen, moderate Lasten',
          applications: 'Elektromotoren, Ventilatoren, Präzisionsinstrumente'
        },
        'EC000031': {
          name: 'Schrägkugellager', 
          technical: 'Kombinierte Belastung, Druckwinkel 15°-40°',
          applications: 'Getriebe, Werkzeugspindeln, Fahrzeugräder'
        }
      }
    },
    'EC0009': {
      name: 'Lineartechnik',
      description: 'Führungssysteme für translatorische Bewegungen',
      subcategories: {
        'EC000924': {
          name: 'Profilschienenführungen',
          technical: 'Wälzkörperumlauf, Genauigkeitsklassen N-UP',
          applications: 'CNC-Maschinen, Robotik, Automatisierung'
        }
      }
    },
    'EC0023': {
      name: 'Dichtungstechnik',
      description: 'Abdichtung gegen Medien und Umwelteinflüsse',
      subcategories: {
        'EC002306': {
          name: 'Radialwellendichtringe',
          technical: 'Dynamische Abdichtung, Materialbeständigkeit kritisch',
          applications: 'Getriebe, Pumpen, Motoren'
        }
      }
    },
    'EC0012': {
      name: 'Antriebstechnik',
      description: 'Kraftübertragungselemente',
      subcategories: {
        'EC001234': {
          name: 'Keilriemen',
          technical: 'Reibschlüssige Übertragung, Keilwirkung',
          applications: 'Kompressoren, Lüfter, Industriemaschinen'
        }
      }
    }
  };

  const handleCategoryClick = (categoryCode) => {
    setSelectedCategory(categoryCode);
    onCategorySelect(categoryCode);
  };

  return (
    <motion.div
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
          <SafeIcon icon={FiGrid} className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900">ETIM-Klassifikation</h3>
          <p className="text-gray-600">Europäischer Technischer Informations-Standard</p>
        </div>
      </div>

      <div className="space-y-4">
        {Object.entries(etimHierarchy).map(([mainCode, mainCategory]) => (
          <div key={mainCode} className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h4 className="font-semibold text-gray-900">{mainCategory.name}</h4>
              <p className="text-sm text-gray-600">{mainCategory.description}</p>
            </div>
            
            <div className="p-4 space-y-3">
              {Object.entries(mainCategory.subcategories).map(([subCode, subCategory]) => {
                const categoryData = categories.find(cat => cat.code === subCode);
                const isSelected = selectedCategory === subCode;
                
                return (
                  <motion.div
                    key={subCode}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      isSelected 
                        ? 'border-red-300 bg-red-50' 
                        : 'border-gray-200 hover:border-red-200 hover:bg-red-50/30'
                    }`}
                    onClick={() => handleCategoryClick(subCode)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded border">
                            {subCode}
                          </span>
                          <h5 className="font-medium text-gray-900">{subCategory.name}</h5>
                          {categoryData && (
                            <span className="text-sm text-gray-500">
                              ({categoryData.count} Produkte)
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{subCategory.technical}</p>
                        <p className="text-xs text-gray-500">
                          <strong>Anwendung:</strong> {subCategory.applications}
                        </p>
                      </div>
                      <SafeIcon icon={FiFilter} className="w-4 h-4 text-gray-400 ml-2" />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-start space-x-3">
          <SafeIcon icon={FiInfo} className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-900 mb-1">ETIM-Standard</h4>
            <p className="text-sm text-blue-800">
              ETIM (Electro Technical Information Model) ist der europäische Standard 
              für die Klassifikation technischer Produkte. Er ermöglicht die 
              einheitliche Beschreibung und den Datenaustausch zwischen Herstellern, 
              Großhandel und Anwendern.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ETIMClassification;